import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { getPaymentUrl } from '@/services/paymentService';
import { toast } from 'react-hot-toast';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { currentUser, loading: authLoading } = useAuth();
  const location = useLocation();
  const [shouldShowToast, setShouldShowToast] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(true);

  useEffect(() => {
    if (!authLoading && !currentUser && !shouldShowToast) {
      setShouldShowToast(true);
    }
  }, [currentUser, authLoading, shouldShowToast]);

  useEffect(() => {
    if (shouldShowToast) {
      toast.error('Please sign in to access this page');
      setShouldShowToast(false);
    }
  }, [shouldShowToast]);

  useEffect(() => {
    async function checkPaymentStatus() {
      if (!currentUser) return;

      try {
        const userRef = doc(db, collections.users, currentUser.uid);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          setIsCheckingPayment(false);
          return;
        }

        const userData = userDoc.data();
        if (!userData.planPaid) {
          // Check if it's not the free plan before redirecting to payment
          if (userData.selectedPlanId !== 'plan-0') {
            const paymentUrl = await getPaymentUrl(userData.selectedPlanId, {
              email: currentUser.email || '',
              firstName: userData.firstName,
              lastName: userData.lastName
            });

            if (paymentUrl) {
              window.location.href = paymentUrl;
              return;
            }
          }
        }

        setIsCheckingPayment(false);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setIsCheckingPayment(false);
      }
    }

    checkPaymentStatus();
  }, [currentUser]);

  // If still loading auth or checking payment but no user is found,
  // redirect to sign in immediately instead of showing loading screen
  if ((authLoading || isCheckingPayment) && !currentUser) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (!currentUser) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Only show loading for logged-in users who are waiting for payment check
  if (isCheckingPayment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return <>{children}</>;
}